import {
  faCheck, faCircleInfo,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { LottieAnimations } from '~/_shared/baseComponents/lottieAnimation';
import { getLottieAnimationDuration } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.helpers';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import { AnimationPreloadComponent } from '~/_shared/components/topUnderbar/statusItem/animationPreload.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { delay } from '~/_shared/utils/delay';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { createReportRemoveItem } from '~/store/frontendState/createReport/createReport.actionCreators';
import { useCreateReportItemsSelector } from '~/store/frontendState/createReport/createReport.selectors';
import { type TopUnderbarItem } from '../item/topUnderbarItem.types';
import { TopUnderbarStatusItemComponent } from './topUnderbarStatus.component';

const exportingAnimationType = LottieAnimationTypes.UploadingImage;
const exportingAnimationProps = {
  segment: LottieAnimations[exportingAnimationType].segments.visor,
  speed: 1,
  size: 36,
  autoplay: true,
  loop: true,
  bothDirections: true,
} as const;

const doneAnimationType = LottieAnimationTypes.CircleDownload;
const doneAnimationProps = {
  segment: LottieAnimations[doneAnimationType].segments.default,
  speed: 1,
  size: 30,
  autoplay: true,
} as const;

const doneDelay = getLottieAnimationDuration(doneAnimationType, doneAnimationProps.segment) / doneAnimationProps.speed;

const animationsToPreload = [
  {
    type: doneAnimationType,
    ...doneAnimationProps,
  }, {
    type: exportingAnimationType,
    ...exportingAnimationProps,
  },
];

const animationPreload = (
  <AnimationPreloadComponent animationsToPreload={animationsToPreload} />
);

export const useReportExportStatusItems = (): ReadonlyArray<TopUnderbarItem> => {
  // items to be displayed as "Download Started"
  const [visibleProcessedItems, setVisibleProcessedItems] = useState<Set<string>>(() => new Set());
  const exportReportItems = useCreateReportItemsSelector();
  const theme = useTheme();
  const [t] = useTranslation();
  const dispatch = useDispatch();

  useEffect(() => {
    exportReportItems.forEach(item => {
      if (item.status !== 'Processed') {
        return;
      }

      setVisibleProcessedItems(prev => {
        const next = new Set(prev);
        next.add(item.hash);
        return next;
      });

      delay(doneDelay).then(() => {
        setVisibleProcessedItems(prev => {
          const next = new Set(prev);
          next.delete(item.hash);
          return next;
        });

        dispatch(createReportRemoveItem(item.hash));
      });
    });
  }, [exportReportItems, dispatch]);

  return useMemo<TopUnderbarItem[]>(() => {
    return exportReportItems.map(item => {
      if (item.status === 'Processing') {
        return {
          key: `export-report-processing-${item.hash}`,
          sticky: true,
          icon: faCircleInfo,
          colors: {
            color: theme.contrasts.selectedAccent2,
            backgroundColor: theme.contrasts.selectedAccent2Background,
          },
          children: (
            <>
              {animationPreload}
              <TopUnderbarStatusItemComponent
                animation={{
                  type: exportingAnimationType,
                  colors: {
                    watermark: theme.iconColors.active,
                    visor: theme.contrasts.selectedAccent2,
                    tick: theme.contrasts.selectedAccent2,
                  },
                  ...exportingAnimationProps,
                }}
                text={{
                  value: `${t('Exporting Report')}...`,
                }}
                suffixText={{
                  value: t('This process can take up to {{toMinutes}} minutes.', { toMinutes: 2 }),
                }}
              />
            </>
          ),
        };
      }

      if (item.status === 'Processed' && visibleProcessedItems.has(item.hash)) {
        return {
          key: `export-report-processed-${item.hash}`,
          sticky: true,
          icon: faCheck,
          colors: {
            color: theme.contrasts.success,
            backgroundColor: theme.contrasts.successBackground,
          },
          children: (
            <>
              {animationPreload}
              <TopUnderbarStatusItemComponent
                animation={{
                  type: doneAnimationType,
                  colors: {
                    fillFrom: theme.contrasts.successBackground,
                    borderFrom: theme.iconColors.borderPrimaryHover,
                    downloadIcon: theme.iconColors.borderPrimaryHover,
                    fillTo: theme.iconColors.backgroundSuccessInverted,
                    borderTo: theme.iconColors.backgroundSuccessInvertedHover,
                    tick: theme.contrasts.successBackground,
                  },
                  ...doneAnimationProps,
                }}
                text={{
                  value: t('Download Started'),
                }}
              />
            </>
          ),
        };
      }

      return null;
    }).filter(notNullsy);
  }, [
    exportReportItems, t, visibleProcessedItems, theme.contrasts.selectedAccent2, theme.iconColors.active,
    theme.contrasts.successBackground, theme.iconColors.backgroundSuccessInverted, theme.iconColors.borderPrimaryHover,
    theme.contrasts.selectedAccent2Background, theme.contrasts.success, theme.iconColors.backgroundSuccessInvertedHover,
  ]);
};
